import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IncomeHourlyResponse } from "@/api/kefc";
import { CityKey } from "@/features/services";
import { Board, fmtHour } from "@/features/forecast/board";
import { DEMAND_COLORS, buildDemand, demandLevel } from "@/features/forecast/demand";
import cls from "./DemandCard.module.scss";

interface Props {
  board: Board;
  hour: number;
  city: CityKey;
  income?: IncomeHourlyResponse;
  status: "idle" | "loading" | "ready" | "error";
}

export const DemandCard: React.FC<Props> = ({ board, hour, city, income, status }) => {
  const { t } = useTranslation();
  const levels = t("demand.levels", { returnObjects: true }) as string[];
  const demand = useMemo(
    () => (income ? buildDemand(income, city, board.tz) : undefined),
    [income, city, board.tz]
  );
  const localHour = board.hours[hour];
  const epochHour = board.startEpoch + hour;

  return (
    <div className={cls.card}>
      <div className={cls.sechead}>
        <span className={cls.title}>{t("compare.demand")}</span>
        <span className={cls.sub}>{fmtHour(localHour)}</span>
      </div>
      {board.series.map((s) => {
        const lv = s.dead ? null : demandLevel(demand, s.code, epochHour, localHour);
        const pending = lv === null && !demand && status === "loading";
        const label = lv === null ? (pending ? "…" : t("compare.noData")) : levels[lv];
        const color =
          lv === null ? "var(--ink-3)" : lv >= 2 ? DEMAND_COLORS[lv] : "var(--ink-2)";
        return (
          <div className={cls.row} key={s.id}>
            <span className={cls.n}>{s.label}</span>
            <span className={cls.dem}>
              {[0, 1, 2, 3].map((i) => (
                <i
                  key={i}
                  style={lv !== null && i <= lv ? { background: DEMAND_COLORS[lv] } : undefined}
                />
              ))}
            </span>
            <span className={cls.lv} style={{ color }}>
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
};
